import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { CaretLeft, ArrowRight, BookOpen } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';

const FadeIn = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.9, delay, ease: [0.22, 0.7, 0.18, 1.0] }}
    className={className}
  >
    {children}
  </motion.div>
);

const CentralMessage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-[#050810] to-[#02020a] text-white relative overflow-hidden" data-testid="central-message-page">
      {/* Ambient background elements */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/3 w-[700px] h-[700px] bg-blue-500/5 rounded-full blur-3xl" />
      </div>
      {/* Grid overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
      }} />

      {user ? <Navbar /> : (
        <div className="max-w-7xl mx-auto px-6 pt-6 relative z-10">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-zinc-400 hover:text-cyan-300 transition-colors"
            data-testid="central-back-btn"
          >
            <CaretLeft size={16} weight="bold" /> Back
          </button>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        {/* 1. Hero */}
        <section className="min-h-[75vh] flex flex-col justify-center py-24">
          <FadeIn>
            <p className="text-[10px] uppercase tracking-[0.4em] text-cyan-300 mb-6">Manifesto</p>
            <h1 className="font-unbounded font-bold text-5xl md:text-7xl leading-[1.05] mb-8 bg-gradient-to-br from-white via-white to-cyan-200 bg-clip-text text-transparent" data-testid="central-hero-headline">
              The world is changing faster than education.
            </h1>
          </FadeIn>
          <FadeIn delay={0.3} className="text-lg md:text-xl text-zinc-400 space-y-1 leading-relaxed max-w-2xl">
            <p>AI is transforming industries.</p>
            <p>Markets move in seconds.</p>
            <p>New careers appear every year.</p>
            <p className="pt-4 text-zinc-300">
              But most students still graduate without understanding how money works.
            </p>
          </FadeIn>
        </section>

        {/* 2. The Shift */}
        <section className="py-24 border-t border-white/5">
          <FadeIn>
            <p className="text-[10px] uppercase tracking-[0.4em] text-emerald-300 mb-4">The shift</p>
            <h2 className="font-unbounded font-bold text-4xl md:text-5xl mb-12">A new world is already here.</h2>
          </FadeIn>
          <div className="space-y-6 text-lg md:text-xl text-zinc-300">
            {[
              'Artificial intelligence is reshaping jobs and industries.',
              'Financial decisions now happen earlier in life than ever before.',
              'Information moves faster than traditional education systems.',
              'Understanding money is becoming a core life skill — not an optional one.',
            ].map((line, i) => (
              <FadeIn key={i} delay={i * 0.15}>
                <p className="border-l-2 border-cyan-400/40 pl-5">{line}</p>
              </FadeIn>
            ))}
          </div>
        </section>

        {/* 3. The Problem */}
        <section className="py-24 border-t border-white/5">
          <FadeIn>
            <p className="text-[10px] uppercase tracking-[0.4em] text-fuchsia-300 mb-4">The problem</p>
            <h2 className="font-unbounded font-bold text-4xl md:text-5xl mb-12">The gap in education.</h2>
          </FadeIn>
          <div className="space-y-4 text-lg text-zinc-300 leading-relaxed">
            <p>Schools teach subjects for exams, not real-world decisions.</p>
            <p>Students learn theory but not financial thinking.</p>
            <p>Investing feels complicated, risky, or inaccessible.</p>
            <p>Most people only learn about money after making expensive mistakes.</p>
          </div>
          <FadeIn delay={0.3}>
            <div className="mt-10 p-6 md:p-8 rounded-2xl border border-cyan-400/30 bg-cyan-400/5 backdrop-blur-sm">
              <p className="text-xl md:text-2xl font-unbounded text-cyan-100 leading-snug">
                &ldquo;By the time most people learn investing, they&apos;re already using real money.&rdquo;
              </p>
            </div>
          </FadeIn>
        </section>

        {/* 4. Core Belief */}
        <section className="py-32 border-t border-white/5 text-center">
          <FadeIn>
            <h2 className="font-unbounded font-bold text-4xl md:text-6xl leading-tight mb-10 bg-gradient-to-b from-white to-emerald-200 bg-clip-text text-transparent">
              We believe financial literacy should start early.
            </h2>
          </FadeIn>
          <FadeIn delay={0.3}>
            <div className="text-lg md:text-xl text-zinc-400 space-y-2">
              <p>Not to create traders.</p>
              <p>Not to push people into markets.</p>
              <p className="text-zinc-200 pt-3">But to build understanding, confidence, and long-term thinking.</p>
            </div>
          </FadeIn>
        </section>

        {/* 5. The Idea */}
        <section className="py-24 border-t border-white/5">
          <FadeIn>
            <p className="text-[10px] uppercase tracking-[0.4em] text-cyan-300 mb-4">The idea</p>
            <h2 className="font-unbounded font-bold text-4xl md:text-5xl mb-8">What TeenVest stands for.</h2>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="text-xl text-zinc-300 mb-3">TeenVest is not a trading app.</p>
            <p className="text-2xl md:text-3xl font-unbounded text-white mb-10">It is a learning environment.</p>
          </FadeIn>
          <div className="space-y-3 text-lg text-zinc-300 mb-10">
            {[
              'Explore investing safely with virtual money.',
              'Learn through AI explanations.',
              'Compete in gamified markets.',
              'Build intuition before real-world exposure.',
            ].map((line, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <p className="flex items-start gap-3">
                  <span className="text-cyan-400 mt-2">&bull;</span> {line}
                </p>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.5}>
            <p className="font-unbounded font-bold text-2xl md:text-3xl text-transparent bg-gradient-to-r from-cyan-300 to-emerald-300 bg-clip-text">
              Learn first. Risk later. Understand always.
            </p>
          </FadeIn>
        </section>

        {/* 6. Philosophy cards */}
        <section className="py-24 border-t border-white/5">
          <FadeIn>
            <p className="text-[10px] uppercase tracking-[0.4em] text-emerald-300 mb-4">Philosophy</p>
            <h2 className="font-unbounded font-bold text-4xl md:text-5xl mb-12">How we teach.</h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              'Learning should feel engaging, not intimidating.',
              'Mistakes should be safe, not costly.',
              'Understanding beats prediction.',
              'Long-term thinking beats short-term guessing.',
            ].map((card, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <motion.div
                  whileHover={{ y: -4 }}
                  className="p-6 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-md hover:border-cyan-400/40 transition-colors"
                >
                  <p className="text-lg text-zinc-200 leading-relaxed">{card}</p>
                </motion.div>
              </FadeIn>
            ))}
          </div>
        </section>

        {/* 7. Future */}
        <section className="py-32 border-t border-white/5">
          <FadeIn>
            <p className="text-[10px] uppercase tracking-[0.4em] text-cyan-300 mb-4">The vision</p>
            <h2 className="font-unbounded font-bold text-4xl md:text-5xl mb-10">The future we see.</h2>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="text-lg text-zinc-400 mb-8">We imagine a future where:</p>
          </FadeIn>
          <div className="space-y-5 text-lg md:text-xl text-zinc-200 leading-loose">
            {[
              'Teenagers understand investing like they understand math.',
              'Financial literacy is taught early in schools.',
              'AI helps people learn — not just predict.',
              'Investing becomes something people understand before they participate in.',
            ].map((line, i) => (
              <FadeIn key={i} delay={0.2 + i * 0.15}>
                <p>&mdash; {line}</p>
              </FadeIn>
            ))}
          </div>
        </section>

        {/* 8. Closing */}
        <section className="py-36 border-t border-white/5 text-center">
          <FadeIn>
            <h2 className="font-unbounded font-bold text-4xl md:text-6xl leading-[1.05] mb-8 bg-gradient-to-br from-white via-cyan-100 to-emerald-200 bg-clip-text text-transparent">
              TeenVest isn&apos;t preparing students for the next exam.<br />It&apos;s preparing them for the next economy.
            </h2>
          </FadeIn>
          <FadeIn delay={0.3}>
            <div className="text-lg text-zinc-400 space-y-2">
              <p>The world will keep changing.</p>
              <p>The question is whether the next generation will understand it.</p>
            </div>
          </FadeIn>
        </section>

        {/* 9. CTA */}
        <section className="py-24 border-t border-white/5 text-center">
          <FadeIn>
            <h3 className="font-unbounded font-bold text-3xl md:text-4xl text-white mb-8">
              Start learning how the financial world works.
            </h3>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              <Link
                to={user ? '/dashboard' : '/'}
                className="flex items-center gap-2 px-8 py-3.5 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400 text-black font-bold text-lg hover:scale-105 transition-transform shadow-[0_0_40px_rgba(56,189,248,0.4)]"
                data-testid="central-cta-get-started"
              >
                Get Started <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/blog"
                className="flex items-center gap-2 px-8 py-3.5 rounded-full border-2 border-white/20 text-white font-bold text-lg hover:bg-white/5 transition-colors"
                data-testid="central-cta-blog"
              >
                <BookOpen size={18} weight="fill" /> Explore Blog
              </Link>
            </div>
            <p className="text-xs text-zinc-500 tracking-widest uppercase">0 real money · 100% learning</p>
          </FadeIn>
        </section>
      </div>
    </div>
  );
};

export default CentralMessage;
